import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { Input } from '@/components/ui';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

/**
 * Image Select configuration interface
 */
interface ImageSelectDisplayConfig {
  layout?: 'grid' | 'carousel' | 'masonry';
  gridColumns?: 2 | 3 | 4 | 5 | 6;
  imageSize?: 'small' | 'medium' | 'large';
  aspectRatio?: '1:1' | '4:3' | '16:9' | '3:4' | 'auto';
  showLabels?: boolean;
  labelPosition?: 'below' | 'overlay' | 'hidden';
  hoverEffect?: 'zoom' | 'border' | 'overlay' | 'none';
  selectedStyle?: 'border' | 'checkmark' | 'overlay' | 'dim-others';
  borderRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  gap?: 'tight' | 'normal' | 'loose';
  mobileColumns?: 1 | 2 | 3;
}

interface ImageOption {
  id: string;
  text: string;
  value: string;
  imageUrl?: string;
  isExclusive?: boolean;
}

// Border radius values
const BORDER_RADIUS_VALUES = {
  none: 'rounded-none',
  small: 'rounded',
  medium: 'rounded-lg',
  large: 'rounded-xl',
  full: 'rounded-full',
} as const;

// Gap values
const GAP_VALUES = {
  tight: 'gap-2',
  normal: 'gap-4',
  loose: 'gap-6',
} as const;

// Image size height classes
const IMAGE_SIZE_CLASSES = {
  small: 'h-20 sm:h-24',
  medium: 'h-28 sm:h-36',
  large: 'h-40 sm:h-52',
} as const;

// Aspect ratio classes
const ASPECT_RATIO_CLASSES = {
  '1:1': 'aspect-square',
  '4:3': 'aspect-[4/3]',
  '16:9': 'aspect-video',
  '3:4': 'aspect-[3/4]',
  auto: '',
} as const;

/**
 * Image selection question component.
 * Allows selecting from image-based options (single or multi-select).
 * Supports multiple layouts, hover effects, and selection styles.
 */
export function ImageSelect({
  question,
  value,
  onChange,
  disabled,
  errors,
  displayOrder,
}: QuestionComponentProps<string | string[] | null>) {
  const [otherText, setOtherText] = useState('');

  // Extract config with defaults
  const config = question.config as ImageSelectDisplayConfig & {
    options?: ImageOption[];
    maxSelections?: number;
    minSelections?: number;
    hasOtherOption?: boolean;
    otherOptionLabel?: string;
  } | undefined;

  const options = (config?.options || []) as ImageOption[];
  const layout = config?.layout || 'grid';
  const gridColumns = config?.gridColumns || 3;
  const maxSelections = config?.maxSelections;
  const minSelections = config?.minSelections || 1;
  const hasOtherOption = config?.hasOtherOption;
  const otherLabel = config?.otherOptionLabel || 'Other';

  // Display settings
  const imageSize = config?.imageSize || 'medium';
  const aspectRatio = config?.aspectRatio || '1:1';
  const showLabels = config?.showLabels !== false;
  const labelPosition = config?.labelPosition || 'below';
  const hoverEffect = config?.hoverEffect || 'zoom';
  const selectedStyle = config?.selectedStyle || 'checkmark';
  const borderRadius = config?.borderRadius || 'medium';
  const gap = config?.gap || 'normal';
  const mobileColumns = config?.mobileColumns || 2;

  // Determine if multi-select based on maxSelections
  const isMultiSelect = maxSelections !== undefined && maxSelections > 1;

  // Apply display order if provided
  const orderedOptions = useMemo(() => {
    if (displayOrder) {
      return displayOrder.map((i) => options[i]).filter(Boolean);
    }
    return options;
  }, [displayOrder, options]);

  // Normalize value to array for multi-select
  const selectedValues: string[] = isMultiSelect
    ? (value as string[]) || []
    : value
    ? [value as string]
    : [];

  const handleSelect = (optionValue: string) => {
    if (disabled) return;

    if (isMultiSelect) {
      let newValues: string[];
      const option = options.find((opt) => opt.value === optionValue);

      if (selectedValues.includes(optionValue)) {
        // Deselect
        newValues = selectedValues.filter((v) => v !== optionValue);
      } else {
        // Check if max selections reached
        if (maxSelections && selectedValues.length >= maxSelections) {
          return;
        }

        // Handle exclusive options
        if (option?.isExclusive) {
          newValues = [optionValue];
        } else {
          // Remove any exclusive options when selecting non-exclusive
          newValues = selectedValues.filter((v) => {
            const opt = options.find((o) => o.value === v);
            return !opt?.isExclusive;
          });
          newValues.push(optionValue);
        }
      }

      onChange(newValues);
    } else {
      // Single select
      onChange(optionValue);
    }
  };

  const handleOtherChange = (text: string) => {
    setOtherText(text);
    const otherValue = `__other__:${text}`;
    if (isMultiSelect) {
      const filtered = selectedValues.filter((v) => !v.startsWith('__other__:'));
      onChange([...filtered, otherValue]);
    } else {
      onChange(otherValue);
    }
  };

  const isSelected = (optValue: string) => selectedValues.includes(optValue);
  const isOtherSelected = selectedValues.some((v) => v.startsWith('__other__:'));
  const hasAnySelection = selectedValues.length > 0;

  // Get grid columns class
  const getGridColumnsClass = () => {
    // Mobile columns
    const mobileClass = mobileColumns === 1 ? 'grid-cols-1' :
                        mobileColumns === 2 ? 'grid-cols-2' : 'grid-cols-3';

    // Desktop columns
    const desktopClass = `sm:grid-cols-${gridColumns}`;

    return `${mobileClass} ${desktopClass}`;
  };

  // Get hover effect classes
  const getHoverClasses = (selected: boolean) => {
    if (disabled) return '';

    switch (hoverEffect) {
      case 'zoom':
        return 'group';
      case 'border':
        return selected ? '' : 'hover:border-gray-400 dark:hover:border-gray-500';
      case 'overlay':
        return '';
      case 'none':
      default:
        return '';
    }
  };

  // Get selected style classes
  const getSelectedClasses = (selected: boolean) => {
    if (!selected) {
      if (selectedStyle === 'dim-others' && hasAnySelection) {
        return 'opacity-50 dark:opacity-40';
      }
      return 'border-gray-200 dark:border-gray-700';
    }

    switch (selectedStyle) {
      case 'border':
        return 'border-primary-500 dark:border-primary-400 ring-2 ring-primary-500 dark:ring-primary-400 ring-offset-2 dark:ring-offset-gray-900';
      case 'checkmark':
        return 'border-primary-500 dark:border-primary-400';
      case 'overlay':
        return 'border-primary-500 dark:border-primary-400';
      case 'dim-others':
        return 'border-primary-500 dark:border-primary-400';
      default:
        return 'border-primary-500 dark:border-primary-400';
    }
  };

  return (
    <QuestionWrapper question={question} errors={errors}>
      {/* Selection info */}
      {isMultiSelect && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Select {minSelections === maxSelections ? minSelections : `${minSelections}-${maxSelections}`} option
          {(maxSelections || 1) > 1 ? 's' : ''}.{' '}
          <span className="text-gray-400 dark:text-gray-500">({selectedValues.length} selected)</span>
        </p>
      )}

      {/* Grid Layout */}
      {layout === 'grid' && (
        <div
          className={cn(
            'grid',
            getGridColumnsClass(),
            GAP_VALUES[gap]
          )}
        >
          {orderedOptions.map((option) => {
            const selected = isSelected(option.value);

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option.value)}
                disabled={disabled}
                className={cn(
                  'relative overflow-hidden border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                  BORDER_RADIUS_VALUES[borderRadius],
                  getHoverClasses(selected),
                  getSelectedClasses(selected),
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {/* Image Container */}
                <div
                  className={cn(
                    'overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center',
                    aspectRatio !== 'auto' ? ASPECT_RATIO_CLASSES[aspectRatio] : IMAGE_SIZE_CLASSES[imageSize]
                  )}
                >
                  {option.imageUrl ? (
                    <img
                      src={option.imageUrl}
                      alt={option.text}
                      className={cn(
                        'h-full w-full object-cover transition-transform duration-300',
                        hoverEffect === 'zoom' && !disabled && 'group-hover:scale-110'
                      )}
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-500">
                      <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}

                  {/* Hover overlay effect */}
                  {hoverEffect === 'overlay' && !disabled && (
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 dark:hover:bg-black/30 transition-colors duration-200" />
                  )}

                  {/* Selected overlay effect */}
                  {selectedStyle === 'overlay' && selected && (
                    <div className="absolute inset-0 bg-primary-500/30 dark:bg-primary-400/30" />
                  )}

                  {/* Overlay label */}
                  {showLabels && labelPosition === 'overlay' && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <span className="text-sm font-medium text-white truncate block">{option.text}</span>
                    </div>
                  )}
                </div>

                {/* Label below image */}
                {showLabels && labelPosition === 'below' && (
                  <div className="p-3 bg-white dark:bg-gray-800">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">{option.text}</span>
                  </div>
                )}

                {/* Checkmark selection indicator */}
                {selectedStyle === 'checkmark' && selected && (
                  <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 dark:bg-primary-400 text-white shadow-md">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                {/* Border selection indicator (shows for border style) */}
                {selectedStyle === 'border' && selected && (
                  <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 dark:bg-primary-400 text-white">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Carousel Layout */}
      {layout === 'carousel' && (
        <div className="relative">
          <div className={cn('flex overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4', GAP_VALUES[gap])}>
            {orderedOptions.map((option) => {
              const selected = isSelected(option.value);

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  disabled={disabled}
                  className={cn(
                    'flex-shrink-0 snap-center relative overflow-hidden border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                    BORDER_RADIUS_VALUES[borderRadius],
                    getHoverClasses(selected),
                    getSelectedClasses(selected),
                    disabled && 'opacity-50 cursor-not-allowed',
                    imageSize === 'small' ? 'w-32' : imageSize === 'medium' ? 'w-44' : 'w-56'
                  )}
                >
                  <div
                    className={cn(
                      'overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center',
                      ASPECT_RATIO_CLASSES[aspectRatio] || 'aspect-square'
                    )}
                  >
                    {option.imageUrl ? (
                      <img
                        src={option.imageUrl}
                        alt={option.text}
                        className={cn(
                          'h-full w-full object-cover transition-transform duration-300',
                          hoverEffect === 'zoom' && !disabled && 'group-hover:scale-110'
                        )}
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-500">
                        <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}

                    {showLabels && labelPosition === 'overlay' && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <span className="text-xs font-medium text-white truncate block">{option.text}</span>
                      </div>
                    )}
                  </div>

                  {showLabels && labelPosition === 'below' && (
                    <div className="p-2 bg-white dark:bg-gray-800">
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate block">{option.text}</span>
                    </div>
                  )}

                  {selectedStyle === 'checkmark' && selected && (
                    <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 dark:bg-primary-400 text-white shadow-md">
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Scroll hint */}
          <div className="flex justify-center mt-2 gap-1">
            {orderedOptions.map((_, idx) => (
              <div
                key={idx}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors',
                  idx === 0 ? 'bg-gray-400 dark:bg-gray-500' : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            ))}
          </div>
        </div>
      )}

      {/* Masonry Layout */}
      {layout === 'masonry' && (
        <div className={cn('columns-2 sm:columns-3', GAP_VALUES[gap])}>
          {orderedOptions.map((option, index) => {
            const selected = isSelected(option.value);
            // Vary heights for masonry effect
            const heights = ['h-32', 'h-40', 'h-48', 'h-36', 'h-44'];
            const heightClass = heights[index % heights.length];

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option.value)}
                disabled={disabled}
                className={cn(
                  'relative w-full mb-4 overflow-hidden border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 break-inside-avoid',
                  BORDER_RADIUS_VALUES[borderRadius],
                  getHoverClasses(selected),
                  getSelectedClasses(selected),
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div
                  className={cn(
                    'overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center',
                    heightClass
                  )}
                >
                  {option.imageUrl ? (
                    <img
                      src={option.imageUrl}
                      alt={option.text}
                      className={cn(
                        'h-full w-full object-cover transition-transform duration-300',
                        hoverEffect === 'zoom' && !disabled && 'group-hover:scale-110'
                      )}
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-500">
                      <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}

                  {showLabels && labelPosition === 'overlay' && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <span className="text-sm font-medium text-white truncate block">{option.text}</span>
                    </div>
                  )}
                </div>

                {showLabels && labelPosition === 'below' && (
                  <div className="p-3 bg-white dark:bg-gray-800">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">{option.text}</span>
                  </div>
                )}

                {selectedStyle === 'checkmark' && selected && (
                  <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 dark:bg-primary-400 text-white shadow-md">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Other option */}
      {hasOtherOption && (
        <div
          className={cn(
            'mt-4 rounded-lg border-2 p-4 transition-all',
            isOtherSelected
              ? 'border-primary-500 dark:border-primary-400'
              : 'border-gray-200 dark:border-gray-700'
          )}
        >
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type={isMultiSelect ? 'checkbox' : 'radio'}
              name={`${question.code}-other`}
              checked={isOtherSelected}
              onChange={() => {
                if (!isOtherSelected) {
                  handleOtherChange(otherText);
                }
              }}
              disabled={disabled}
              className="h-5 w-5 text-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600"
            />
            <span className="text-sm text-gray-900 dark:text-gray-100">{otherLabel}</span>
          </label>
          {isOtherSelected && (
            <Input
              type="text"
              value={otherText}
              onChange={(e) => handleOtherChange(e.target.value)}
              placeholder="Please specify..."
              disabled={disabled}
              className="mt-3 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          )}
        </div>
      )}
    </QuestionWrapper>
  );
}
